import React, { useEffect, useState } from "react";
import { Radio, Typography } from "@optimizely/axiom";
import classnames from "classnames";

import { RoleTypeInstanceSelector } from "./RoleTypeInstanceSelector";
import { ProductAttribute } from "../../../domain/ProductAttribute";

import { RoleAttribute } from "../../../domain/RoleAttribute.interface";
import { IOrganizationProductInstance, OrganizationProduct } from "../../../domain/OrganizationProduct";
import { Role } from "../../../domain/Role";
import { CUSTOMER_DEFINED_INSTANCE_ROLE, CUSTOMER_DEFINED_PRODUCT_ROLE } from "../../../constants/constants";

import styles from "./ExplicitRoleTypeFormFields.module.scss";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";

export type RoleType = typeof CUSTOMER_DEFINED_INSTANCE_ROLE | typeof CUSTOMER_DEFINED_PRODUCT_ROLE;

type RoleTypeSelectionProps = {
    className?: string;
    disabled?: boolean;
    handleRoleTypeChange: ({ roleType }: { roleType: RoleType }) => void;
    value?: RoleType;
    handleCmpRoleSelection: ({
        preMappedValues,
        id,
        key,
        values,
        clearOtherAttributes
    }: {
        id?: string;
        key?: string;
        preMappedValues?: boolean;
        values: string[] | RoleAttribute[];
        clearOtherAttributes?: boolean;
    }) => void;
    selectedInstances: IOrganizationProductInstance[];
    selectedProduct: OrganizationProduct | null;
    setSelectedInstances: React.Dispatch<React.SetStateAction<IOrganizationProductInstance[]>>;
    editing: boolean;
    role: Role | undefined;
    selectedInstanceTarget: (IOrganizationProductInstance | undefined)[];
};

export const RoleTypeSelection = ({
    className,
    disabled = false,
    handleRoleTypeChange,
    value = undefined,
    handleCmpRoleSelection,
    selectedInstances,
    selectedProduct,
    setSelectedInstances,
    editing,
    role,
    selectedInstanceTarget
}: RoleTypeSelectionProps) => {
    const [roleType, setRoleType] = useState<RoleType | undefined>(value);
    const [cmpTeamAttribute, setCmpTeamAttribute] = useState<ProductAttribute | undefined>(undefined);
    const [isTeamRoleSelected, setIsTeamRoleSelected] = useState<boolean>(false);
    const { enabled: teamGroupsEnabled = false } = useFeatureFlag(Flags.ENABLE_TEAM_GROUPS);

    const isCmpSelected = selectedProduct?.id === process.env.REACT_APP_CMP_PRODUCT_ID;

    const classes = classnames(styles["explicit-role-type-form-fields"], className);

    useEffect(() => {
        if (roleType) {
            handleRoleTypeChange({ roleType });
        }
    }, [handleRoleTypeChange, roleType, setSelectedInstances]);

    useEffect(() => {
        const handleEvent = (e: CustomEvent) => {
            const { cmpTeamAttributeEnabled: cmpEnabled, cmpTeamAttribute: cmp } = e.detail;
            setCmpTeamAttribute(cmp);
            setIsTeamRoleSelected(cmpEnabled);
        };

        window.addEventListener("cmp-role", handleEvent as EventListenerOrEventListenerObject);

        return () => {
            window.removeEventListener("cmp-role", handleEvent as EventListenerOrEventListenerObject);
        };
    }, []);

    return (
        <div className={classes}>
            <Typography type="header4" tag="div">
                <span style={{ fontWeight: 300 }}>
                    Select Role Type <span aria-label="(required)" className="oui-label--required" />
                </span>
            </Typography>
            <Typography type="body">
                Role type determines how the role can be assigned. Once saved, role type cannot be changed.
            </Typography>
            <Radio
                checked={roleType === CUSTOMER_DEFINED_PRODUCT_ROLE}
                isDisabled={disabled}
                name="product"
                label="Product Role"
                className={styles["explicit-role-type-form-fields__radio"]}
                onChange={() => {
                    setSelectedInstances([]);
                    setRoleType(CUSTOMER_DEFINED_PRODUCT_ROLE);
                    if (isTeamRoleSelected) {
                        handleCmpRoleSelection({ values: [] });
                    }
                    setIsTeamRoleSelected(false);
                }}
            />
            <Typography type="body" tag="div" className="push-triple--left push--bottom">
                This role will be available to use for any instance of this product for your organization,{" "}
                <span className="weight--bold style--italic">
                    including instances that may be created in the future.
                </span>
            </Typography>
            <>
                <Radio
                    checked={roleType === CUSTOMER_DEFINED_INSTANCE_ROLE && !isTeamRoleSelected}
                    isDisabled={disabled}
                    name="instance"
                    label="Instance Role"
                    onChange={() => {
                        setSelectedInstances([]);
                        setRoleType(CUSTOMER_DEFINED_INSTANCE_ROLE);
                        if (isTeamRoleSelected) {
                            handleCmpRoleSelection({ values: [] });
                        }
                        setIsTeamRoleSelected(false);
                    }}
                />
                <Typography type="body" tag="div" className="push-triple--left push--bottom">
                    This role will <span className="weight--bold style--italic">only</span> be available to use for the
                    instances selected. Once saved, you may change the instances selected, but may not change the role
                    type.
                </Typography>
                <div className="push-triple--left push--bottom">
                    <RoleTypeInstanceSelector
                        selectedInstances={selectedInstances}
                        selectedProduct={selectedProduct}
                        setSelectedInstances={setSelectedInstances}
                        editing={editing}
                        role={role}
                        selectedInstanceTarget={selectedInstanceTarget}
                        roleType={roleType}
                        disabled={isTeamRoleSelected}
                    />
                </div>
            </>
            {isCmpSelected && cmpTeamAttribute && !teamGroupsEnabled && (
                <>
                    <Radio
                        checked={roleType === CUSTOMER_DEFINED_INSTANCE_ROLE && isTeamRoleSelected}
                        isDisabled={disabled}
                        name="team"
                        label="Team Role"
                        onChange={() => {
                            setRoleType(CUSTOMER_DEFINED_INSTANCE_ROLE);
                            if (!isTeamRoleSelected) {
                                setSelectedInstances([]);
                                handleCmpRoleSelection({
                                    id: cmpTeamAttribute?.id,
                                    key: cmpTeamAttribute?.key,
                                    values: cmpTeamAttribute?.values,
                                    clearOtherAttributes: true
                                });
                            }
                            setIsTeamRoleSelected(true);
                        }}
                    />
                    <Typography type="body" tag="div" className="push-triple--left push--bottom">
                        This role will <span className="weight--bold style--italic">only</span> be available to use for
                        the <span className="weight--bold style--italic">one</span> instance selected. Team Roles do not
                        support permissions. Once saved, you <span className="weight--bold style--italic">cannot</span>{" "}
                        change the instance selected.
                    </Typography>
                    <div className="push-triple--left">
                        <RoleTypeInstanceSelector
                            selectedInstances={selectedInstances}
                            selectedProduct={selectedProduct}
                            setSelectedInstances={setSelectedInstances}
                            editing={editing}
                            role={role}
                            selectedInstanceTarget={selectedInstanceTarget}
                            roleType={roleType}
                            disabled={!!role || (!role && !isTeamRoleSelected)}
                            isTeamRoleDropdown
                            isTeamRoleSelected={isTeamRoleSelected}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

RoleTypeSelection.displayName = "RoleTypeSelection";
